import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataEventType, SourceDataFieldType, Trial } from "@/api/dtos";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialContext from "@/contexts/data/TrialContext";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useFormVersionConfiguration from "@/hooks/form/configuration/useFormVersionConfiguration";
import useItemConfiguration from "@/hooks/form/configuration/useItemConfiguration";
import useItemGroupConfiguration from "@/hooks/form/configuration/useItemGroupConfiguration";
import useItemValueConfiguration from "@/hooks/form/configuration/useItemValueConfiguration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Chip, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Portal, Tooltip, styled, useTheme, darken, lighten, IconButton, Theme, alpha, listItemButtonClasses, listItemSecondaryActionClasses } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, Fragment, ReactNode, useContext, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link as RouteLink, useLocation, useParams, unstable_useBlocker } from "react-router-dom";
import sourceDataFieldTypeToText from "@/utilities/configuration/sourceDataFieldTypeToText";
import sourceDataItemGroupTypeToText from "@/utilities/configuration/sourceDataItemGroupTypeToText";
import { ILink } from "@/components/layouts/DefaultLayout";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-duotone-svg-icons";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import { createEvents } from "@/hooks/form/source-data/useEvents";
import { createForms } from "@/hooks/form/source-data/useForms";
import { LoadingContext } from "@/contexts/layouts/LoadingContext";
import ParticipantSourceDataEventStatusesContext from "@/contexts/data/ParticipantSourceDataEventStatusesContext";
import ParticipantSourceDataFormStatusesContext from "@/contexts/data/ParticipantSourceDataFormStatusesContext";
import { StatusListItemTooltip, StatusListItemTooltipStats } from "@/components/lists/status/StatusListStats";

const ProgressBox = styled(Box)(({ theme }) => ({
    position: 'relative'
}));

const ReviewProgressBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    background: alpha(theme.palette.success.main, 0.10),
    zIndex: 3,
    pointerEvents: 'none'
}));

const VerificationProgressBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    background: alpha(theme.palette.success.main, 0.10),
    zIndex: 2,
    pointerEvents: 'none'
}));

const ItemProgressBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    zIndex: 1
}));


const IndentBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8),
    borderLeft: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)}`
}));

const IndentDivider = styled(Divider)(({ theme }) => ({
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)
}));

const NavList = styled(List)(({ theme }) => ({ 
    display: 'flex', 
    flexDirection: 'column', 
    height: '100%'
}));

const RepeatBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    whiteSpace: 'pre',
    minHeight: theme.spacing(4),
    minWidth: theme.spacing(8),
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.mode === 'dark' ?
        alpha(theme.palette.common.white, 0.12):
        alpha(theme.palette.common.black, 0.12),
    background: theme.palette.mode === 'dark' ?
        theme.palette.grey[800] :
        theme.palette.grey[100],

    '& > *': {
        minWidth: theme.spacing(4),
        textAlign: 'center'
    }
}))

const RepeatIconBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    alignSelf: 'stretch',
    textAlign: 'center',
    minWidth: theme.spacing(4),
    borderStyle: 'solid',
    borderWidth: 0,
    borderRightWidth: 1,
    borderRadius: 0,
    borderColor: theme.palette.mode === 'dark' ?
        alpha(theme.palette.common.white, 0.12):
        alpha(theme.palette.common.black, 0.12),
    background: theme.palette.mode === 'dark' ?
        theme.palette.grey[700] :
        theme.palette.grey[200]
}))


export const StyledListItem = styled(ListItem)(({ theme }) => ({
    [`& > .${listItemButtonClasses.root}`]: {
        paddingRight: theme.spacing(0)
    },
    [`& > .${listItemSecondaryActionClasses.root}`]: {
        right: theme.spacing(0)
    }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(6)
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

interface ISourceDataLink {
    url?: string;
    icon: any;
    newTab: boolean;
    route: boolean;
    title?: string;
    additional?: ReactNode;
    type: string;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    allowReview?: boolean;
    allowVerification?: boolean;
    completeReview?: number;
    totalReview?: number;
    reviewPercentage?: number;
    completeVerification?: number;
    totalVerification?: number;
    verificationPercentage?: number;
    totalPercentage?: number;
    links?: ISourceDataLink[];
}

interface ISourceDataNavigationProps {
    preview?: boolean
}

const drawEnabled = true;

const CreateLinks: FC<{
    links: ISourceDataLink[]; 
    theme: Theme;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
}> = ({ 
    links, 
    theme,  
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid
}) => {
    const list = useMemo(() => {
        return links?.map((link, i) => {
            const active = studyEventOid === link.studyEventOid && studyEventRepeat === link.studyEventRepeat && caseReportFormOid === link.caseReportFormOid; //i + 1 === links.length;

            let linkComponent = null;
            if (link.newTab || !link.route) {
                linkComponent = (
                    <StyledListItem
                        disablePadding
                    >
                        <StyledListItemButton
                            //@ts-ignore-next-line
                            component="a" 
                            href={link.url}
                            key={i}
                        >
                            <StyledListItemIcon 
                            >
                                <FontAwesomeIcon fixedWidth icon={link.icon} />
                            </StyledListItemIcon>
                            <StyledListItemText 
                                primary={link.title}
                            />
                            {link.additional}
                        </StyledListItemButton>
                    </StyledListItem>
                );
            }
            else {
                linkComponent = (
                    <StyledListItem
                        disablePadding
                    >
                        <StyledListItemButton
                            //@ts-ignore-next-line
                            component={RouteLink}
                            to={link.url ?? ''}
                            sx={{
                                background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined
                            }} 
                            key={i}
                        >
                            <StyledListItemIcon 
                            >
                                <FontAwesomeIcon fixedWidth icon={link.icon} />
                            </StyledListItemIcon>
                            <StyledListItemText 
                                primary={link.title}
                            />
                            {link.additional}
                        </StyledListItemButton>
                    </StyledListItem>
                );
            }
            
            //return linkComponent;
            return (
                <Fragment key={i}>
                    <ProgressBox>
                        {
                            !!link.reviewPercentage && (
                                <ReviewProgressBox sx={{width: `${link.reviewPercentage}%`}}>

                                </ReviewProgressBox>
                            )
                        }
                        {
                            !!link.verificationPercentage && (
                                <VerificationProgressBox sx={{width: `${link.verificationPercentage}%`}}>
                                </VerificationProgressBox>
                            )
                        }
                        <ItemProgressBox>
                            <StatusListItemTooltip
                                placement="left"
                                title={
                                    <>
                                        <strong>SDV Progress</strong>
                                        {
                                            link.allowReview && (
                                                <StatusListItemTooltipStats>
                                                    <span>
                                                        Reviewed
                                                    </span>
                                                    <span>
                                                        {!!link.totalReview && <>({link.completeReview} / {link.totalReview})</>} {link.reviewPercentage}%
                                                    </span>
                                                </StatusListItemTooltipStats>
                                            )
                                        }
                                        {
                                            link.allowVerification && (
                                                <StatusListItemTooltipStats>
                                                    <span>
                                                        Verified
                                                    </span>
                                                    <span>
                                                    {!!link.totalVerification && <>({link.completeVerification} / {link.totalVerification})</>} {link.verificationPercentage}%
                                                    </span>
                                                </StatusListItemTooltipStats>
                                            )
                                        }
                                        <StatusListItemTooltipStats>
                                            <span>
                                                Total
                                            </span>
                                            <span>
                                                {link.totalPercentage}%
                                            </span>
                                        </StatusListItemTooltipStats>
                                    </>
                                }
                            >
                                {linkComponent}
                            </StatusListItemTooltip>
                        </ItemProgressBox>
                    </ProgressBox>
                    {((i + 1) < links.length || link.links) && <Divider /> }
                    {
                        link.links && (
                            <>
                                <IndentBox>
                                    <CreateLinks 
                                        links={link.links} 
                                        theme={theme} 
                                        studyEventOid={studyEventOid}
                                        studyEventRepeat={studyEventRepeat ? +studyEventRepeat : undefined}
                                        caseReportFormOid={caseReportFormOid}
                                    />
                                </IndentBox>
                                <IndentDivider />
                            </>
                        )
                    }
                </Fragment>
            );
        });
    }, [
        links, 
        theme,  
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    ])

    return (
        <NavList disablePadding>
            {
                list
            }
        </NavList>
    )
}


const SourceDataNavigation: FC<ISourceDataNavigationProps> = ({
    preview
}) => {
    const { data: trial } = useDeferredValue(useContext(TrialContext));
    const { data: masterGroup } = useDeferredValue(useContext(MasterGroupContext));
    const { data: coordinatingGroup } = useDeferredValue(useContext(CoordinatingGroupContext));
    const { data: institution } = useDeferredValue(useContext(InstitutionContext));
    const { data: participant } = useDeferredValue(useContext(ParticipantContext));
    const { data: metadata } = useDeferredValue(useContext(MetadataContext));
    const { data: trialConfiguration } = useDeferredValue(useContext(TrialConfigurationSourceDataContext));
    const { data: configuration } = useDeferredValue(useContext(ConfigurationSourceDataContext));
    const { data: sourceData } = useDeferredValue(useContext(SourceDataContext));
    const { idMap: eventStatuses } = useDeferredValue(useContext(ParticipantSourceDataEventStatusesContext));
    const { idMap: formStatuses } = useDeferredValue(useContext(ParticipantSourceDataFormStatusesContext));

    const [drawerWidth, setDrawerWidth] = useState(60);

    const { studyEventOid, studyEventRepeat, caseReportFormOid } = useParams();

    const [drawOpen, setDrawOpen] = useState(true);
    
    const theme = useTheme();

    const container = useMemo(() => {
        return document.getElementById('post-content');
    }, [])

    const configurationSourceDataId = configuration?.configuration?.id;

    const links: ISourceDataLink[] = useMemo(() => {

        const l: ISourceDataLink[] = [];

        const events = createEvents(
            metadata, 
            trialConfiguration, 
            configuration, 
            sourceData, 
            trial, 
            masterGroup, 
            coordinatingGroup, 
            institution,
            participant, 
            preview
        );

        if (events) {
            for (let event of events) {
                const forms = createForms(
                    metadata, 
                    trialConfiguration, 
                    configuration, 
                    sourceData, 
                    trial, 
                    masterGroup, 
                    coordinatingGroup, 
                    institution,
                    participant,
                    event?.studyEventOid,
                    event?.studyEventRepeat,
                    preview
                )

                const eventStatus = event?.studyEventOid && event?.studyEventRepeat ?
                    eventStatuses?.[event.studyEventOid]?.[event.studyEventRepeat] :
                    undefined;

                const childLinks = forms?.map(form => {
                    const formStatus = event?.studyEventOid && event?.studyEventRepeat && form?.caseReportFormOid ?
                        formStatuses?.[event.studyEventOid]?.[event.studyEventRepeat]?.[form.caseReportFormOid] :
                        undefined;

                    const formAllowReview = event?.configuration?.allowReview && form?.configuration?.allowReview && form?.formVersion?.configuration?.allowReview;
                    const formAllowVerification = event?.configuration?.allowVerification && form?.configuration?.allowVerification && form?.formVersion?.configuration?.allowVerification;

                    let formReviewPercentage = Math.floor(formStatus?.reviewTotalCount ? ((formStatus?.reviewCompleteCount ?? 0) + (formStatus?.reviewNotApplicableCount ?? 0)) / (formStatus.reviewTotalCount) * 100 : 100);
                    let formVerificationPercentage = Math.floor(formStatus?.verificationTotalCount ? ((formStatus?.verificationCompleteCount ?? 0) + (formStatus?.verificationNotApplicableCount ?? 0)) / (formStatus.verificationTotalCount) * 100 : 100)
                    let formTotalPercentage = formStatus?.reviewTotalCount || formStatus?.verificationTotalCount ? 
                        Math.floor(((formStatus?.reviewCompleteCount ?? 0) + (formStatus?.reviewNotApplicableCount ?? 0) + ((formStatus?.verificationCompleteCount ?? 0) + (formStatus?.verificationNotApplicableCount ?? 0))) / ((formStatus?.reviewTotalCount ?? 0) + (formStatus?.verificationTotalCount ?? 0)) * 100) : 
                        100;

                    if (!formAllowReview && !formAllowVerification) {
                        formReviewPercentage = 100;
                        formVerificationPercentage = 100;
                        formTotalPercentage = 100;
                    }
                    else if (!formAllowReview) {
                        formReviewPercentage = formVerificationPercentage;
                        formTotalPercentage = formVerificationPercentage;
                    }
                    else if (!formAllowVerification) {
                        formVerificationPercentage = formReviewPercentage;
                        formTotalPercentage = formReviewPercentage;
                    }

                    return {
                        type: 'Form',
                        icon: AppSettings.icons.form,
                        url: preview ? form?.previewLink : form?.dataLink,
                        title: form?.displayName,
                        route: true,
                        newTab: false,
                        studyEventOid: form?.studyEventOid,
                        studyEventRepeat: form?.studyEventRepeat,
                        caseReportFormOid: form?.caseReportFormOid,

                        allowReview: formAllowReview,
                        allowVerification: formAllowVerification,
                
                        completeReview: (formStatus?.reviewCompleteCount ?? 0) + (formStatus?.reviewNotApplicableCount ?? 0),
                        completeVerification: (formStatus?.verificationCompleteCount ?? 0) + (formStatus?.verificationNotApplicableCount ?? 0),
                        totalReview: formStatus?.reviewTotalCount ?? 0,
                        totalVerification: formStatus?.verificationTotalCount ?? 0,
                        reviewPercentage: formReviewPercentage,
                        verificationPercentage: formVerificationPercentage,
                        totalPercentage: formTotalPercentage,
                    }
                });

                
                const eventAllowReview = event?.configuration?.allowReview && childLinks?.some(x => x.allowReview);
                const eventAllowVerification = event?.configuration?.allowVerification && childLinks?.some(x => x.allowVerification);

                let eventReviewPercentage = Math.floor(eventStatus?.reviewTotalCount ? ((eventStatus?.reviewCompleteCount ?? 0) + (eventStatus?.reviewNotApplicableCount ?? 0)) / (eventStatus.reviewTotalCount) * 100 : 100);
                let eventVerificationPercentage = Math.floor(eventStatus?.verificationTotalCount ? ((eventStatus?.verificationCompleteCount ?? 0) + (eventStatus?.verificationNotApplicableCount ?? 0)) / (eventStatus.verificationTotalCount) * 100 : 100)
                let eventTotalPercentage = eventStatus?.reviewTotalCount || eventStatus?.verificationTotalCount ? 
                    Math.floor(((eventStatus?.reviewCompleteCount ?? 0) + (eventStatus?.verificationCompleteCount ?? 0) + (eventStatus?.reviewNotApplicableCount ?? 0) + (eventStatus?.verificationNotApplicableCount ?? 0)) / ((eventStatus?.reviewTotalCount ?? 0) + (eventStatus?.verificationTotalCount ?? 0)) * 100) : 
                    100;

                if (!eventAllowReview && !eventAllowVerification) {
                    eventReviewPercentage = 100;
                    eventVerificationPercentage = 100;
                    eventTotalPercentage = 100;
                }
                else if (!eventAllowReview) {
                    eventReviewPercentage = eventVerificationPercentage;
                    eventTotalPercentage = eventVerificationPercentage;
                }
                else if (!eventAllowVerification) {
                    eventVerificationPercentage = eventReviewPercentage;
                    eventTotalPercentage = eventReviewPercentage;
                }

                l.push({
                    type: 'Event',
                    icon: AppSettings.icons.event,
                    url: preview ? event?.previewLink : event?.dataLink,
                    title: event?.displayName,
                    route: true,
                    newTab: false,
                    studyEventOid: event?.studyEventOid,
                    studyEventRepeat: event?.studyEventRepeat,
                    caseReportFormOid: undefined,
                    
                    allowReview: eventAllowReview,
                    allowVerification: eventAllowVerification,

                    completeReview: (eventStatus?.reviewCompleteCount ?? 0) + (eventStatus?.reviewNotApplicableCount ?? 0),
                    completeVerification: (eventStatus?.verificationCompleteCount ?? 0) + (eventStatus?.verificationNotApplicableCount ?? 0),
                    totalReview: eventStatus?.reviewTotalCount ?? 0,
                    totalVerification: eventStatus?.verificationTotalCount ?? 0,
                    reviewPercentage: eventReviewPercentage,
                    verificationPercentage: eventVerificationPercentage,
                    totalPercentage: eventTotalPercentage,
                    
                    additional:  event?.eventMetadata?.type === SourceDataEventType.Repeating && (
                        <Tooltip title={<>Event Repeat &mdash; {event?.studyEventRepeat}</>}>
                            <RepeatBox>
                                <RepeatIconBox>
                                    <FontAwesomeIcon icon={AppSettings.icons.studyEventRepeat} size="sm" />
                                </RepeatIconBox>
                                <Box>
                                    <small>
                                        {event?.studyEventRepeat}
                                    </small>
                                </Box>
                            </RepeatBox>
                        </Tooltip>
                    ),

                    links: childLinks
                });
            }
        }

        return l;
    }, [trial, metadata, trialConfiguration, configuration, sourceData, preview, eventStatuses, formStatuses]);

    const isOpen = drawOpen && links && links.length > 0;

    const calculatedWidth = parseInt(theme.spacing(drawerWidth));
    const calculatedExpandButtonWidth = parseInt(theme.spacing(4.5));

    return (
        <Portal
            container={container}
        >
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    right: isOpen ? calculatedWidth - (calculatedExpandButtonWidth / 2) : 0,
                    width: theme.spacing(4.5),
                    '&:hover *': {
                        visibility: 'visible'
                    },
                    zIndex: 1201
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: 0,
                        margin: 'auto 0',
                        height: theme.spacing(4.5)
                    }}
                >
                    <Button
                        sx={{
                            minWidth: 0,
                            width: theme.spacing(4.5),
                            height: theme.spacing(4.5),
                            visibility: 'hidden',
                            padding: 0
                        }}
                        onClick={() => setDrawOpen(C => !C)}
                        color="inherit"
                        variant="contained"
                    >
                        <FontAwesomeIcon fixedWidth icon={isOpen ? faChevronRight : faChevronLeft} />
                    </Button>
                </Box>
            </Box>
            {
                drawEnabled && (
                    <Drawer
                        open={isOpen}
                        variant="persistent"
                        anchor="right"
                        sx={{
                            width: isOpen ? theme.spacing(drawerWidth) : 0,
                            flexShrink: 0,
                            transition: isOpen ? 
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }) :
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.leavingScreen,
                                }),
                            overflowX: isOpen ? 'hidden' : 'hidden',
                            [theme.breakpoints.up('sm')]: isOpen ? 
                                undefined : 
                                {
                                    //width: theme.spacing(8),
                                },
                            '& .MuiPaper-root': {
                                width: isOpen ? theme.spacing(drawerWidth) : 0,
                                transition: isOpen ? 
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen,
                                    }) :
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.leavingScreen,
                                    }),
                                overflowX: isOpen ? 'hidden' : 'hidden',
                                [theme.breakpoints.up('sm')]: isOpen ? 
                                    undefined : 
                                    {
                                        //width: theme.spacing(8),
                                    },
                            }
                        }}
                        // classes={{
                        //     paper: clsx({
                        //         [classes.drawerOpen]: drawOpen,
                        //         [classes.drawerClose]: !drawOpen,
                        //     }),
                        // }}
                    >
                        <Box 
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: theme.spacing(0, 1),
                                // necessary for content to be below app bar
                                ...(theme.mixins.toolbar as any),
                                minHeight: `${theme.spacing(8)} !important`
                            }}
                        />
                        <Box
                            sx={{
                                flex: '1 1 auto',
                                overflow: 'auto'
                            }}
                        >
                            <CreateLinks 
                                links={links} 
                                theme={theme}
                                studyEventOid={studyEventOid}
                                studyEventRepeat={studyEventRepeat ? +studyEventRepeat : undefined}
                                caseReportFormOid={caseReportFormOid}
                            />
                        </Box>
                    </Drawer>
                )
            }
        </Portal>
    );
};

export default SourceDataNavigation;